import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { tagService } from '../../services/tagService';
import ActionMenu from '../../components/common/ActionMenu';
import Loader from '../../components/Loader';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Pagination from '../../components/common/Pagination';

const LIMIT = 10;

const SortIcon = ({ direction }) => {
   if (!direction)
      return (
         <svg
            className="w-3.5 h-3.5 opacity-40"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
         >
            <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
         </svg>
      );
   return (
      <svg
         className="w-3.5 h-3.5"
         fill="none"
         stroke="currentColor"
         strokeWidth="2"
         viewBox="0 0 24 24"
      >
         {direction === 'asc' ? <path d="M5 15l7-7 7 7" /> : <path d="M19 9l-7 7-7-7" />}
      </svg>
   );
};

const Tags = () => {
   const [tags, setTags] = useState([]);
   const [loading, setLoading] = useState(true);
   const [page, setPage] = useState(1);
   const [search, setSearch] = useState('');
   const [blogsSort, setBlogsSort] = useState(null); // null | 'asc' | 'desc'
   const [showForm, setShowForm] = useState(false);
   const [editingId, setEditingId] = useState(null);
   const [formData, setFormData] = useState({ name: '' });
   const [saving, setSaving] = useState(false);
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [deleting, setDeleting] = useState(false);

   useEffect(() => {
      fetchTags();
   }, []);

   const fetchTags = async () => {
      try {
         setLoading(true);
         const response = await tagService.getAll(true);
         setTags(response.data);
      } catch (error) {
         console.error('Error fetching tags:', error);
      } finally {
         setLoading(false);
      }
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.name.trim()) {
         toast.error('Tag name is required');
         return;
      }

      try {
         setSaving(true);
         if (editingId) {
            await tagService.update(editingId, formData);
            toast.success('Tag updated successfully');
         } else {
            await tagService.create(formData);
            toast.success('Tag created successfully');
         }
         setShowForm(false);
         setEditingId(null);
         setFormData({ name: '' });
         setPage(1);
         fetchTags();
      } catch (error) {
         console.error('Error saving tag:', error);
         toast.error(error.response?.data?.message || 'Failed to save tag');
      } finally {
         setSaving(false);
      }
   };

   const handleEdit = (tag) => {
      setEditingId(tag.id);
      setFormData({ name: tag.name });
      setShowForm(true);
   };

   const handleDeleteClick = (tag) => {
      if ((tag.blogCount || 0) > 0) {
         toast.error(
            `Cannot delete "${tag.name}". It has ${tag.blogCount} blog(s) attached. Remove blogs first.`
         );
         return;
      }
      setDeleteTarget(tag);
   };

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeleting(true);
         await tagService.delete(deleteTarget.id);
         toast.success('Tag deleted successfully');
         setDeleteTarget(null);
         setPage(1);
         fetchTags();
      } catch (error) {
         console.error('Error deleting tag:', error);
         toast.error(error.response?.data?.message || 'Failed to delete tag');
      } finally {
         setDeleting(false);
      }
   };

   const handleCancel = () => {
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '' });
   };

   const handleBlogsSortToggle = () => {
      setBlogsSort((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
      setPage(1);
   };

   const handleSearchChange = (e) => {
      setSearch(e.target.value);
      setPage(1);
   };

   if (loading) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   const q = search.trim().toLowerCase();
   const filtered = q
      ? tags.filter((t) => t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q))
      : tags;

   const sorted =
      blogsSort === null
         ? filtered
         : [...filtered].sort((a, b) =>
              blogsSort === 'asc'
                 ? (a.blogCount || 0) - (b.blogCount || 0)
                 : (b.blogCount || 0) - (a.blogCount || 0)
           );

   const totalPages = Math.ceil(sorted.length / LIMIT);
   const paginatedTags = sorted.slice((page - 1) * LIMIT, page * LIMIT);

   return (
      <PanelLayout>
         <div className="space-y-6">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Tags</h1>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">Manage blog tags</p>
               </div>
               <Button onClick={() => setShowForm(true)}>Create New Tag</Button>
            </div>

            {/* Search */}
            <div className="max-w-sm">
               <Input
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search by name or slug…"
               />
            </div>

            <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
               <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                     <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Slug
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           <button
                              onClick={handleBlogsSortToggle}
                              className="flex items-center gap-1.5 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                           >
                              Blogs
                              <SortIcon direction={blogsSort} />
                           </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Actions
                        </th>
                     </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                     {paginatedTags.length === 0 ? (
                        <tr>
                           <td
                              colSpan="4"
                              className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                           >
                              No tags found
                           </td>
                        </tr>
                     ) : (
                        paginatedTags.map((tag) => (
                           <tr
                              key={tag.id}
                              onClick={() => handleEdit(tag)}
                              className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                           >
                              <td className="px-6 py-4 whitespace-nowrap">
                                 <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {tag.name}
                                 </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                 <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {tag.slug}
                                 </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                 <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {tag.blogCount || 0}
                                 </span>
                              </td>
                              <td
                                 className="px-6 py-4 whitespace-nowrap"
                                 onClick={(e) => e.stopPropagation()}
                              >
                                 <ActionMenu
                                    items={[
                                       { label: 'Edit', onClick: () => handleEdit(tag) },
                                       {
                                          label: 'Delete',
                                          variant: 'danger',
                                          onClick: () => handleDeleteClick(tag),
                                       },
                                    ]}
                                 />
                              </td>
                           </tr>
                        ))
                     )}
                  </tbody>
               </table>
            </div>

            <Pagination
               page={page}
               totalPages={totalPages}
               total={sorted.length}
               limit={LIMIT}
               onPageChange={setPage}
            />
         </div>

         <Modal
            isOpen={showForm}
            onClose={handleCancel}
            title={editingId ? 'Edit Tag' : 'Create New Tag'}
         >
            <form onSubmit={handleSubmit} className="space-y-4">
               <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                     value={formData.name}
                     onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                     placeholder="Tag name"
                     required
                  />
               </div>
               <div className="flex justify-end gap-3">
                  <Button type="button" variant="secondary" size="sm" onClick={handleCancel}>
                     Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={saving}>
                     {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                  </Button>
               </div>
            </form>
         </Modal>

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete Tag"
            message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={deleting}
         />
      </PanelLayout>
   );
};

export default Tags;
